import {Column, dummySelectWrapper, lookupNestedProp, setNestedProp, TID} from "./Table";
import {getEditable, getEditKey} from "./TableEditCell";
import {Calculator} from "./Calculator";
import {autoFormat} from "../../pages/project/manage/summary/AutoFormat";

export class PasteError extends Error {
    rowLabel: string;
    column: string;

    constructor(rowLabel: string, column: string, message: string) {
        super(`${rowLabel}, ${column}: ${message}`);
        this.rowLabel = rowLabel;
        this.column = column;
    }
}

export function pasteRowLabel(row: any, index: number): string {
    if(typeof row?.name === "string" && row.name.trim() !== "") return row.name;
    return `row ${index + 1}`;
}

// applyPastedRow builds the updated row for a paste (or a range delete) and runs the same
// per-cell hooks a single-cell edit runs, so a paste can't store a value an edit would reject.
// cells[i] is the clipboard text for columns[i]. Cells are applied left to right, and then
// every pasted select column is checked against its options using the finished row, because
// the options can depend on other cells in the same row (e.g. handing depends on opening type).
export function applyPastedRow<T extends TID>(columns: Column<T>[], src: T, cells: string[], rowLabel: string): T {
    const obj = Object.assign({}, src);
    const pasted: Column<T>[] = [];

    cells.forEach((strValue, index) => {
        if(index >= columns.length) return;
        const column = columns[index];

        // a disabled cell can't be edited, so a paste into it is ignored
        if(column.disabled && column.disabled(obj)) return;

        try {
            applyPastedCell(column, obj, src, strValue);
        } catch (e: any) {
            if(e instanceof PasteError) throw e;
            throw new PasteError(rowLabel, column.name, e?.message || e?.toString() || "invalid value");
        }

        pasted.push(column);
    });

    for(const column of pasted) {
        checkSelectOption(column, obj, rowLabel);
    }

    return obj;
}

function applyPastedCell<T extends TID>(column: Column<T>, obj: T, src: T, strValue: string) {
    const editKey = getEditKey(column, obj);
    const editable = getEditable(column, obj);

    if(editable?.paste) {
        editable.paste(obj, strValue);
        return;
    }

    if(!editKey) {
        if(editable?.type === "button") return;
        if(column.editObj) column.editObj(obj, src, strValue);
        return;
    }

    // @ts-ignore
    const current = lookupNestedProp(obj, editKey);
    let value: any = strValue;

    if(editable?.parseValue) {
        value = editable.parseValue(current, strValue);
    } else if(editable?.type === "number" || typeof current === "number") {
        value = strValue.trim() === "" ? null : Calculator.eval(strValue);
    } else if(editable?.type === "checkbox" || typeof current === "boolean") {
        value = strValue.toLowerCase() === "true";
    }

    if(editable?.validate) {
        editable.validate(value);
    }

    if(editable?.type === "string" && editable.autoFormat) {
        value = autoFormat(value, editable.autoFormat);
    }

    if(editable?.onChangeBeforeSave) {
        editable.onChangeBeforeSave(obj, strValue);
    }

    if(column.editObj) {
        column.editObj(obj, src, value);
        return;
    }

    // @ts-ignore
    setNestedProp(obj, editKey, value);
}

// checkSelectOption rejects a pasted select value that isn't in the column's options for the
// finished row, and normalizes a value that matches an option's value or display text
// (case-insensitive) to the option's value.
function checkSelectOption<T extends TID>(column: Column<T>, obj: T, rowLabel: string) {
    const editable = getEditable(column, obj);
    if(!editable || editable.type !== "select" || editable.freeSolo) return;

    const editKey = getEditKey(column, obj);
    if(!editKey) return;

    // @ts-ignore
    const value = lookupNestedProp(obj, editKey);
    if(value === null || value === undefined || value === "") return;

    const options = editable.options(dummySelectWrapper(obj)).filter(o => !o.subHeading && !o.disabled);
    if(options.some(o => o.value === value)) return;

    const str = String(value).trim().toLowerCase();
    const match = options.find(o => String(o.value).toLowerCase() === str || o.display.toLowerCase() === str);
    if(match) {
        // @ts-ignore
        setNestedProp(obj, editKey, match.value);
        return;
    }

    throw new PasteError(rowLabel, column.name, `"${value}" is not a valid option`);
}

// pasteErrorSummary joins a list of paste errors into one message, limited to the first few.
export function pasteErrorSummary(errors: PasteError[], limit = 5): string {
    const shown = errors.slice(0, limit).map(e => e.message);
    if(errors.length > limit) {
        shown.push(`and ${errors.length - limit} more`);
    }

    return "Paste cancelled. " + shown.join("; ");
}
